import { Button, Group, Paper, TextInput, Title } from "@mantine/core";
import { useForm } from "@mantine/form";
import { UserFields } from "../types/user.types";
import useCurrentUser from "../hooks/use-current-user";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { mtsUrl } from "../lib/mts-url";
import csrfToken from "../lib/csrf-token";
import { useRef } from "react";
import { notifications } from "@mantine/notifications";
import { IconCheck, IconX } from "@tabler/icons-react";

export default function Settings() {
  const queryClient = useQueryClient();
  const loaded = useRef(false);
  const currentUser = useCurrentUser();
  const form = useForm<Partial<UserFields>>({
    mode: 'uncontrolled',
  });

  const query = useQuery({
    queryKey: ['users', currentUser.id],
    queryFn: () => axios.get(mtsUrl(`users/${currentUser.id}`)).then(r => r.data)
  });

  const updateUser = useMutation({
    mutationFn: (user: Partial<UserFields>) => {
      return axios.put(mtsUrl(`users/${currentUser.id}/update`),
        { user },
        {
          withCredentials: true,
          headers: { 'X-CSRF-Token': csrfToken() }
        }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users', currentUser.id] });
      queryClient.invalidateQueries({ queryKey: ['requests', 'all'] });
      
      notifications.show({
        message: `Your settings were successfully updated.`,
        icon: <IconCheck />,
        color: 'teal',
        position: 'top-center'
      });
    },
    onError: (error) => {
      notifications.show({
        message: `There was an error saving your changes.`,
        icon: <IconX />,
        color: 'red',
        position: 'top-center'
      });
    }
  });

  const onFormSubmit = (user: Partial<UserFields>) => {
    updateUser.mutate(user);
  };

  if (query.isSuccess && !loaded.current) {
    loaded.current = true;
    form.setInitialValues(query.data);
    form.reset();
  }
  
  return (
    <Paper radius={6} p={12} mt="lg" withBorder>
      <Title>Settings</Title>

      <form onSubmit={form.onSubmit(onFormSubmit)}>
        <TextInput
          {...form.getInputProps('username')}
          key={form.key('username')}
          label="Username"
          disabled
        />
        <TextInput
          {...form.getInputProps('name')}
          key={form.key('name')}
          label="Name"
        />
        <TextInput
          {...form.getInputProps('email')}
          key={form.key('email')}
          label="Email"
          required
        />
        <TextInput
          {...form.getInputProps('department')}
          key={form.key('department')}
          label="Department"
        />
        <Group justify="flex-end" mt="md" bg="gray.1" p="sm">
          <Button type="submit">Save</Button>
        </Group>
      </form>
    </Paper>
  );
}